import type { GuestContactMethod } from '@repo/api-contracts';
import { type ReactElement, useId } from 'react';
import { cn, FormField } from '@/shared/ui';

export interface GuestContactFieldsProps {
  readonly method: GuestContactMethod;
  readonly email: string;
  readonly phone: string;
  readonly emailError?: string | undefined;
  readonly phoneError?: string | undefined;
  readonly onMethodChange: (method: GuestContactMethod) => void;
  readonly onEmailChange: (email: string) => void;
  readonly onPhoneChange: (phone: string) => void;
}

interface ContactOption {
  readonly value: GuestContactMethod;
  readonly label: string;
  readonly description: string;
}

const OPTIONS: readonly ContactOption[] = [
  { value: 'EMAIL', label: '이메일', description: '견적서 링크를 메일로 받습니다' },
  { value: 'PHONE', label: '휴대폰', description: '담당자가 카카오톡으로 연락드립니다' },
];

/** 칸 높이 44px — 휴대폰에서도 누르기 쉬운 크기(회원 견적 요청 폼과 같다). */
const INPUT_CLASS = 'h-11';

/**
 * 비회원 연락 방법(비회원 견적 설계 G12) — 이메일 또는 휴대폰 중 하나만 고른다. 고른 방법의 칸만 보이고 그 칸만 필수다
 * (다른 칸에 적어 둔 값은 화면 상태에만 남고, 제출 훅이 고른 방법의 값만 싣는다).
 * 선택지는 44px 이상 높이의 카드형 라디오이고 고른 카드는 검은 테두리다. 칸 이름(이메일 주소·휴대폰 번호)은 선택지 이름과 겹치지 않게 짓는다.
 */
export const GuestContactFields = ({
  method,
  email,
  phone,
  emailError,
  phoneError,
  onMethodChange,
  onEmailChange,
  onPhoneChange,
}: GuestContactFieldsProps): ReactElement => {
  const name = useId();

  return (
    <div className="flex flex-col gap-5">
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-3 text-sm leading-none font-medium">
          연락 받을 방법
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {OPTIONS.map((option) => (
            <label
              key={option.value}
              className={cn(
                'flex min-h-14 cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 text-sm transition-colors',
                method === option.value
                  ? 'border-foreground'
                  : 'border-input hover:border-foreground/40',
              )}
            >
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={method === option.value}
                className="mt-0.5 size-4 shrink-0 cursor-pointer accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onChange={() => onMethodChange(option.value)}
              />
              <span className="flex flex-col gap-1">
                <span className="font-medium">{option.label}</span>
                <span className="text-xs text-muted-foreground">{option.description}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {method === 'EMAIL' ? (
        <FormField
          key="email"
          label="이메일 주소"
          type="email"
          inputMode="email"
          autoComplete="email"
          isRequired
          required
          description="견적서 링크를 이 주소로 보내 드립니다."
          placeholder="name@company.com"
          className={INPUT_CLASS}
          value={email}
          error={emailError}
          onChange={(event) => onEmailChange(event.target.value)}
        />
      ) : (
        <FormField
          key="phone"
          label="휴대폰 번호"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          isRequired
          required
          description="카카오톡으로 연락드립니다."
          placeholder="010-1234-5678"
          className={INPUT_CLASS}
          value={phone}
          error={phoneError}
          onChange={(event) => onPhoneChange(event.target.value)}
        />
      )}
    </div>
  );
};
